/**
 * StepPropulsion - Wizard Step 3: Propulsion system and fire protection
 * Uses actual PropulsionConfigurationSelector and EnginePropulsionSection logic
 * Preserves all existing conditional logic and validation
 */

import React from 'react';
import { Zap, Shield, AlertTriangle } from 'lucide-react';
import WizardSection from '../WizardSection';
import PropulsionConfigurationSelector from '../../tabs/components/PropulsionConfigurationSelector';
import FireProtectionInformation from '../../tabs/components/FireProtectionInformation';

const StepPropulsion = ({ draftConfig, onUpdate }) => {
  // Check if this is a non-self-propelled platform
  const isNonSelfPropelled = draftConfig.vesselType === 'non_self_propelled' || 
                             draftConfig.baseCertificate === 'non_self_propelled';

  // Create updateConfig function compatible with existing components
  const updateConfig = (updates) => {
    if (typeof updates === 'function') {
      onUpdate(updates(draftConfig));
    } else {
      onUpdate(updates);
    }
  };

  // Extract propulsion details for fire protection logic
  const getPropulsionDetails = () => {
    if (draftConfig.propulsionConfiguration) {
      const config = draftConfig.propulsionConfiguration;
      const parts = config.split('_');
      
      // Determine base propulsion type
      const baseType = parts[0] === 'hybrid' ? 'battery_hybrid' : 
                      parts[0] === 'battery' ? 'battery_electric' :
                      parts[0];
      
      // For battery electric, there's no above/below deck distinction
      // For hybrid, the config tells us diesel location
      const engineLocation = parts.includes('above') ? 'above_deck' : 
                            parts.includes('below') ? 'below_deck' : 
                            baseType === 'battery_electric' ? null : 'above_deck';
      
      return {
        propulsionType: baseType,
        enginePosition: parts.includes('outboard') ? 'outboard' : 'inboard',
        engineLocation: engineLocation,
        method: parts.includes('shaft') ? 'shaft' : 
                parts.includes('jet') ? 'water_jet' :
                parts.includes('pod') ? 'pod_drive' : 
                parts.includes('outboard') ? 'outboard' : 'shaft'
      };
    }
    
    // Fallback to legacy fields for migration
    return {
      propulsionType: draftConfig.propulsionType || 'diesel',
      enginePosition: draftConfig.enginePosition || 'inboard',
      engineLocation: draftConfig.engineLocation || 'above_deck',
      method: draftConfig.propulsionMethod || 'shaft'
    };
  };

  const details = getPropulsionDetails();
  
  // Check if fire protection choice should be shown
  const isPureBatteryElectric = details.propulsionType === 'battery_electric';
  const isOutboard = details.enginePosition === 'outboard';
  const isHybrid = details.propulsionType === 'battery_hybrid';
  
  // For hybrids and diesel, check engine location
  // Battery electric doesn't have engine location (only motor type)
  const isInboardAboveDeck = !isPureBatteryElectric && 
    details.enginePosition === 'inboard' && 
    details.engineLocation === 'above_deck';
  
  const isInboardBelowDeck = !isPureBatteryElectric && 
    details.enginePosition === 'inboard' && 
    details.engineLocation === 'below_deck';
  
  // Fire protection choice only for above deck inboard conventional/hybrid diesel engines
  // Below deck = mandatory fixed system (no choice)
  // Pure battery electric = no diesel engine (battery always needs fixed system)
  // Outboard = portable only (no choice)
  const showFireProtectionChoice = !isPureBatteryElectric && !isOutboard && isInboardAboveDeck;

  return (
    <WizardSection>
      {/* Step Introduction */}
      <div className="text-center mb-6">
        <div className="mx-auto flex items-center justify-center h-14 w-14 md:h-12 md:w-12 rounded-full bg-orange-100 mb-3">
          <Zap className="h-7 w-7 md:h-6 md:w-6 text-orange-600" />
        </div>
        <h2 className="text-xl md:text-xl font-bold text-gray-900 mb-2">
          {isNonSelfPropelled ? 'Platform Configuration' : 'Propulsion & Power'}
        </h2>
        <WizardSection.Text className="max-w-2xl mx-auto">
          {isNonSelfPropelled 
            ? 'Non-self-propelled platforms require minimal configuration as they are towed or pushed by another vessel.'
            : 'Configure your vessel\'s propulsion system, steering, and power systems. This determines engine maintenance requirements and fire protection needs.'
          }
        </WizardSection.Text>
      </div>

      {/* Conditional Content Based on Platform Type */}
      {isNonSelfPropelled ? (
        <>
          {/* Platform-specific notice */}
          <WizardSection.Callout variant="info" title="Non-Self-Propelled Platform - WBC3 Section 26.5">
            Non-self-propelled platforms follow the selective assessment framework per WBC3 Section 26.5.1.1. 
            These platforms do not have their own propulsion and are towed or pushed by another vessel.
          </WizardSection.Callout>

          {/* Platform Configuration Card */}
          <WizardSection.Card>
            <WizardSection.Header>Platform Configuration</WizardSection.Header>
            
            <div className="space-y-4">
              <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                <div className="flex items-start">
                  <Shield className="h-5 w-5 text-purple-600 mt-0.5 mr-3 flex-shrink-0" />
                  <div>
                    <h4 className="text-sm font-medium text-purple-900 mb-1">
                      Propulsion System: Not Applicable
                    </h4>
                    <p className="text-sm text-purple-700">
                      Non-self-propelled platforms do not have engines, propulsion systems, or associated fire protection requirements. 
                      The platform is moved by a separate towing or pushing vessel.
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex items-start">
                  <AlertTriangle className="h-5 w-5 text-blue-600 mt-0.5 mr-3 flex-shrink-0" />
                  <div>
                    <h4 className="text-sm font-medium text-blue-900 mb-1">
                      Platform Requirements Only
                    </h4>
                    <p className="text-sm text-blue-700">
                      This platform will show only the minimal equipment requirements specific to non-self-propelled 
                      platforms (approximately 15 items) rather than full manned vessel requirements.
                    </p>
                  </div>
                </div>
              </div>

              {/* Basic platform details if needed */}
              <WizardSection.Field
                label="Platform Type"
                help="Type of non-self-propelled platform"
              >
                <WizardSection.Select
                  value={draftConfig.platformType || 'work_platform'}
                  onChange={(e) => updateConfig({ platformType: e.target.value })}
                >
                  <option value="work_platform">Work Platform</option>
                  <option value="accommodation_platform">Accommodation Platform</option>
                  <option value="storage_platform">Storage Platform</option>
                  <option value="barge">Barge</option>
                  <option value="other">Other Platform Type</option>
                </WizardSection.Select>
              </WizardSection.Field>

              <WizardSection.Field
                label="Primary Use"
                help="Main operational purpose of the platform"
              >
                <WizardSection.Input
                  type="text"
                  value={draftConfig.platformUse || ''}
                  onChange={(e) => updateConfig({ platformUse: e.target.value })}
                  placeholder="e.g., Offshore maintenance platform"
                />
              </WizardSection.Field>
            </div>
          </WizardSection.Card>
        </>
      ) : (
        <>
          {/* Standard vessel propulsion configuration */}
          <WizardSection.Callout variant="regulatory" title="WBC3 Propulsion Requirements">
            Engine configuration affects maintenance schedules, fire protection requirements, and manning. 
            Petrol engines have specific restrictions per WBC3 Section 8.6.1.
          </WizardSection.Callout>

          {/* Propulsion Configuration - Uses the actual sophisticated selector */}
          <WizardSection.Card>
            <WizardSection.Header>Propulsion Configuration</WizardSection.Header>
            <div className="mt-4">
              <PropulsionConfigurationSelector 
                vesselConfig={draftConfig} 
                updateConfig={updateConfig}
              />
            </div>
          </WizardSection.Card>

          {/* Engine Details */}
          <WizardSection.Card>
            <WizardSection.Header>Engine Details</WizardSection.Header>
        
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <WizardSection.Field
                label="Number of Engines/Motors"
                help="Total propulsion units installed"
              >
                <WizardSection.Input
                  type="number"
                  value={draftConfig.numberOfEngines || 1}
                  onChange={(e) => updateConfig({ numberOfEngines: parseInt(e.target.value) || 1 })}
                  placeholder="e.g., 2"
                  min="1"
                  step="1"
                />
              </WizardSection.Field>

              <WizardSection.Field
                label="Total Power (kW)"
                help="Combined power of all propulsion units"
              >
                <WizardSection.Input
                  type="number"
                  value={draftConfig.enginePower || ''}
                  onChange={(e) => updateConfig({ enginePower: parseFloat(e.target.value) || '' })}
                  placeholder="e.g., 350"
                  min="0"
                  step="1"
                />
              </WizardSection.Field>

              <WizardSection.Field
                label="Maximum Speed (knots)"
                help="Maximum operational speed"
              >
                <WizardSection.Input
                  type="number"
                  value={draftConfig.maxSpeed || ''}
                  onChange={(e) => updateConfig({ maxSpeed: parseFloat(e.target.value) || '' })}
                  placeholder="e.g., 25"
                  min="0"
                  step="0.1"
                />
              </WizardSection.Field>

              <WizardSection.Field
                label="Steering System *"
                help="Primary steering control type per WBC3 Section 10.2"
              >
                <WizardSection.Select
                  value={draftConfig.steeringSystem || draftConfig.steeringType || ''}
                  onChange={(e) => updateConfig({ 
                    steeringSystem: e.target.value,
                    steeringType: e.target.value // Backward compatibility
                  })}
                >
                  <option value="">Select system...</option>
                  <option value="hydraulic">Hydraulic</option>
                  <option value="cable">Cable/Mechanical</option>
                  <option value="electric">Electric</option>
                  <option value="rudder">Rudder (Direct)</option>
                  {/* Conditional options for special cases */}
                  {/* Only show tiller for small vessels */}
                  {draftConfig.lengthOverall && draftConfig.lengthOverall < 12 && (
                    <option value="tiller">Tiller (vessels &lt;12m only)</option>
                  )}
                  {/* Only show remote control for unmanned vessels */}
                  {draftConfig.vesselType === 'unmanned' && (
                    <option value="remote">Remote Control (ROUV only)</option>
                  )}
                </WizardSection.Select>
                
                {/* WBC3 Section 10.2 Guidance - Accurate per RA verification */}
                <div className="mt-2 text-xs text-gray-500">
                  {draftConfig.steeringSystem === 'remote' ? (
                    <span className="text-amber-600 font-medium">
                      Remote control system - Emergency steering required per WBC3 10.2.3. 
                      Emergency drill must be demonstrated to Certifying Authority.
                    </span>
                  ) : (draftConfig.steeringSystem === 'hydraulic' || draftConfig.steeringType === 'hydraulic' || 
                      draftConfig.steeringSystem === 'electric' || draftConfig.steeringType === 'electric' ||
                      draftConfig.steeringSystem === 'cable' || 
                      draftConfig.steeringSystem === 'rudder' || draftConfig.steeringType === 'rudder' ||
                      draftConfig.steeringSystem === 'tiller') ? (
                    <span>
                      Direct control system - No emergency steering requirements per WBC3 10.2
                    </span>
                  ) : !draftConfig.steeringSystem ? (
                    <span>Select steering type to determine requirements</span>
                  ) : null}
                </div>
              </WizardSection.Field>
            </div>

            {/* High-Speed Warning */}
            {draftConfig.maxSpeed >= 20 && (
              <div className="mt-4">
                <WizardSection.Callout variant="warning" title="High-Speed Classification">
                  ⚠️ Vessels ≥20 knots require enhanced equipment and operational requirements per WBC3.
                  Additional safety equipment and crew training apply.
                </WizardSection.Callout>
              </div>
            )}
          </WizardSection.Card>

          {/* Additional Systems */}
          <WizardSection.Card>
            <WizardSection.Header>Power & Control Systems</WizardSection.Header>
            
            <div className="space-y-4">
              <div>
                <label className="flex items-center space-x-3">
                  <input
                    type="checkbox"
                    checked={draftConfig.hasEmergencySteering || false}
                    onChange={(e) => updateConfig({ hasEmergencySteering: e.target.checked })}
                    className="h-4 w-4 text-cyan-600 focus:ring-cyan-600 border-gray-300 rounded"
                  />
                  <div>
                    <span className="text-sm font-medium text-gray-900">Emergency Steering System</span>
                    <p className="text-xs text-gray-500">Backup steering independent of main system</p>
                  </div>
                </label>
              </div>

              <div>
                <label className="flex items-center space-x-3">
                  <input
                    type="checkbox"
                    checked={draftConfig.bilge_auto || false}
                    onChange={(e) => updateConfig({ bilge_auto: e.target.checked })}
                    className="h-4 w-4 text-cyan-600 focus:ring-cyan-600 border-gray-300 rounded"
                  />
                  <div>
                    <span className="text-sm font-medium text-gray-900">Automatic Bilge System</span>
                    <p className="text-xs text-gray-500">Automatic bilge pump with level sensors</p>
                  </div>
                </label>
              </div>

              <div>
                <label className="flex items-center space-x-3">
                  <input
                    type="checkbox"
                    checked={draftConfig.power_critical_systems || false}
                    onChange={(e) => updateConfig({ power_critical_systems: e.target.checked })}
                    className="h-4 w-4 text-cyan-600 focus:ring-cyan-600 border-gray-300 rounded"
                  />
                  <div>
                    <span className="text-sm font-medium text-gray-900">Power-Critical Systems</span>
                    <p className="text-xs text-gray-500">Systems requiring uninterrupted power (navigation, safety)</p>
                  </div>
                </label>
              </div>

              <div>
                <label className="flex items-center space-x-3">
                  <input
                    type="checkbox"
                    checked={draftConfig.hasShorepower || false}
                    onChange={(e) => updateConfig({ hasShorepower: e.target.checked })}
                    className="h-4 w-4 text-cyan-600 focus:ring-cyan-600 border-gray-300 rounded"
                  />
                  <div>
                    <span className="text-sm font-medium text-gray-900">Shore Power Connection</span>
                    <p className="text-xs text-gray-500">Shore power inlet for charging and hotel loads</p>
                  </div>
                </label>
              </div>
            </div>
          </WizardSection.Card>

          {/* Battery System Configuration - for electric and hybrid vessels */}
          {(details.propulsionType === 'battery_electric' || details.propulsionType === 'battery_hybrid') && (
            <WizardSection.Card>
              <WizardSection.Header>
                Battery System Configuration
                {details.propulsionType === 'battery_hybrid' && (
                  <span className="ml-2 text-sm font-normal text-gray-600">(Electric Component)</span>
                )}
              </WizardSection.Header>
              
              <WizardSection.Callout variant="regulatory" title="WBC3 Annex 1 Requirements">
                Battery propulsion systems require mandatory fixed fire suppression, detection systems, 
                and minimum 6 air changes per hour ventilation. Configuration determines specific requirements.
              </WizardSection.Callout>

              <div className="mt-4 space-y-4">
                {/* Battery Type - Required per WBC3 8.4.2 */}
                <WizardSection.Field
                  label="Battery Chemistry Type *"
                  help="WBC3 Section 8.4.2 - Different requirements for lithium vs lead-acid"
                >
                  <WizardSection.Select
                    value={draftConfig.batteryType || ''}
                    onChange={(e) => updateConfig({ 
                      batteryType: e.target.value,
                      hasLithiumBatteries: e.target.value === 'lithium-ion'
                    })}
                  >
                    <option value="">Select battery type...</option>
                    <option value="lithium-ion">Lithium-ion</option>
                    <option value="lead-acid">Lead-acid</option>
                    <option value="other">Other (requires Administration approval)</option>
                  </WizardSection.Select>
                  {draftConfig.batteryType === 'other' && (
                    <div className="mt-2 p-2 bg-amber-50 border border-amber-200 rounded-md">
                      <p className="text-xs text-amber-800">
                        ⚠️ Other battery types require Administration approval per WBC3 8.4.2
                      </p>
                    </div>
                  )}
                </WizardSection.Field>

                {/* Battery Location - Critical for requirements per Annex 1 Section 1.3 */}
                <WizardSection.Field
                  label="Battery Installation Location *"
                  help="Determines ventilation and fire protection requirements per Annex 1 Section 1.3"
                >
                  <WizardSection.Select
                    value={draftConfig.batteryLocation || ''}
                    onChange={(e) => updateConfig({ 
                      batteryLocation: e.target.value,
                      // Battery room requires active ventilation per 1.3.2
                      ventilationType: e.target.value === 'battery_room' ? 'active' : draftConfig.ventilationType
                    })}
                  >
                    <option value="">Select location...</option>
                    <option value="battery_box">Battery Box (Enclosed container)</option>
                    <option value="battery_room">Battery Room (Dedicated compartment)</option>
                  </WizardSection.Select>
                </WizardSection.Field>

                {/* Ventilation Type - Required per Annex 1 Section 1.3.1 */}
                <WizardSection.Field
                  label="Ventilation Type *"
                  help="Minimum 6 air changes per hour required per Annex 1 Section 1.3"
                >
                  <WizardSection.Select
                    value={draftConfig.ventilationType || ''}
                    onChange={(e) => updateConfig({ ventilationType: e.target.value })}
                    disabled={draftConfig.batteryLocation === 'battery_room'} // Battery rooms MUST have active
                  >
                    <option value="">Select ventilation...</option>
                    <option value="active">Active (Mechanical ventilation)</option>
                    <option value="passive" disabled={draftConfig.batteryLocation === 'battery_room'}>
                      Passive (Natural ventilation)
                    </option>
                  </WizardSection.Select>
                  {draftConfig.batteryLocation === 'battery_room' && (
                    <p className="mt-1 text-xs text-amber-600">
                      Battery rooms MUST have active ventilation per WBC3 Annex 1 Section 1.3.2
                    </p>
                  )}
                </WizardSection.Field>

                {/* Charging Capability - Affects requirements per Annex 1 Sections 2.11 & 2.12 */}
                <div className="space-y-3">
                  {details.propulsionType === 'battery_hybrid' && (
                    <div>
                      <label className="flex items-center space-x-3">
                        <input
                          type="checkbox"
                          checked={draftConfig.selfCharging || false}
                          onChange={(e) => updateConfig({ selfCharging: e.target.checked })}
                          className="h-4 w-4 text-cyan-600 focus:ring-cyan-600 border-gray-300 rounded"
                        />
                        <div>
                          <span className="text-sm font-medium text-gray-900">Self-Charging Capability</span>
                          <p className="text-xs text-gray-500">Diesel engine can charge batteries (affects Annex 1 Section 2.12)</p>
                        </div>
                      </label>
                    </div>
                  )}
                  
                  <div>
                    <label className="flex items-center space-x-3">
                      <input
                        type="checkbox"
                        checked={draftConfig.shoreCharging || false}
                        onChange={(e) => updateConfig({ shoreCharging: e.target.checked })}
                        className="h-4 w-4 text-cyan-600 focus:ring-cyan-600 border-gray-300 rounded"
                      />
                      <div>
                        <span className="text-sm font-medium text-gray-900">Shore Charging Capability</span>
                        <p className="text-xs text-gray-500">Shore power connection for charging (affects Annex 1 Section 2.11)</p>
                      </div>
                    </label>
                  </div>
                </div>

                {/* Mandatory Fire Protection Notice */}
                <WizardSection.Callout variant="warning" title="Mandatory Fire Protection">
                  <p className="text-sm">
                    ⚠️ Fixed fire suppression system is MANDATORY for all battery installations per WBC3 Annex 1 Section 3.4.1.
                    This is in addition to any engine compartment fire protection.
                  </p>
                  <ul className="mt-2 text-sm space-y-1">
                    <li>• Automatic fixed suppression system (no choice)</li>
                    <li>• Smoke, heat AND flame detectors required</li>
                    <li>• Gas detection if LEL risk assessment indicates</li>
                  </ul>
                </WizardSection.Callout>
              </div>
            </WizardSection.Card>
          )}

          {/* Fire Protection Configuration - For conventional/hybrid diesel component */}
          {draftConfig.propulsionConfiguration && (
            <WizardSection.Card>
              <WizardSection.Header>
                {details.propulsionType === 'battery_hybrid' ? 'Engine Fire Protection (Diesel Component)' : 'Fire Protection Configuration'}
              </WizardSection.Header>
              
              {details.propulsionType === 'battery_hybrid' && (
                <p className="text-sm text-gray-600 mb-3">
                  Standard WBC3 Section 16 requirements apply to the diesel engine component
                </p>
              )}
              
              <div className="mt-4">
                <FireProtectionInformation
                  showChoice={showFireProtectionChoice}
                  isOutboard={isOutboard}
                  isPureBatteryElectric={isPureBatteryElectric}
                  isInboardBelowDeck={isInboardBelowDeck}
                  currentPropulsionType={details.propulsionType}
                  currentEngineLocation={details.engineLocation}
                  vesselConfig={draftConfig}
                  updateConfig={updateConfig}
                />
              </div>
            </WizardSection.Card>
          )}
        </>
      )}

      {/* Help Information */}
      <WizardSection.Callout variant="info" title="What's Next?">
        {isNonSelfPropelled 
          ? 'Next, we\'ll configure platform spaces and basic facilities. Since this is a non-self-propelled platform, most accommodation and propulsion-related requirements will not apply.'
          : 'After configuring propulsion and power systems, we\'ll set up vessel spaces, accommodation, and cooking facilities which determine fire protection requirements.'
        }
      </WizardSection.Callout>
    </WizardSection>
  );
};

export default StepPropulsion;